/**
 * Thin wrappers around the orval-generated agent-prompts hooks.
 *
 * Same unwrap pattern as useDatasets — the generated hooks return
 * `{data, status, headers}` per orval v8's fetch client. Mutations centralize
 * cache invalidation so the editor doesn't repeat queryKey plumbing, and the
 * two writes both invalidate the LIST as well as the version history: saving
 * a revision changes which body is live.
 */

import { useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import {
  getListPromptsApiAgentPromptsGetQueryKey,
  getListVersionsApiAgentPromptsKindVersionsGetQueryKey,
  useActivateVersionApiAgentPromptsKindVersionsVersionActivatePost,
  useCreateVersionApiAgentPromptsKindVersionsPost,
  useListPromptsApiAgentPromptsGet,
  useListVersionsApiAgentPromptsKindVersionsGet,
  useTestPromptApiAgentPromptsKindTestPost,
} from '@/api/generated/agent-prompts/agent-prompts'
import type {
  activateVersionApiAgentPromptsKindVersionsVersionActivatePostResponse,
  testPromptApiAgentPromptsKindTestPostResponse,
} from '@/api/generated/agent-prompts/agent-prompts'
import type {
  AgentPromptCreate,
  AgentPromptResponse,
  AgentPromptVersionResponse,
  PromptTestRequest,
} from '@/api/generated/model'

export type AgentPrompt = AgentPromptResponse
export type AgentPromptVersion = AgentPromptVersionResponse

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

interface AgentPromptsOptions {
  /**
   * Re-read the list every 15s and whenever the tab regains focus, for as
   * long as the caller is mounted. The app's query defaults keep a list
   * fresh for five minutes with no focus refetch, so without this a save
   * made in another tab never reaches an open editor.
   */
  watchLive?: boolean
}

const WATCH_LIVE_INTERVAL_MS = 15_000

export function useAgentPrompts(options?: AgentPromptsOptions): UnwrappedQuery<AgentPrompt[]> {
  const query = useListPromptsApiAgentPromptsGet({
    query:
      options?.watchLive === true
        ? { refetchInterval: WATCH_LIVE_INTERVAL_MS, refetchOnWindowFocus: 'always' }
        : {},
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function usePromptVersions(kind: string): UnwrappedQuery<AgentPromptVersion[]> {
  const query = useListVersionsApiAgentPromptsKindVersionsGet(kind)
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

function useInvalidatePrompt(): (kind: string) => void {
  const queryClient = useQueryClient()
  return (kind: string) => {
    void queryClient.invalidateQueries({
      queryKey: getListPromptsApiAgentPromptsGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getListVersionsApiAgentPromptsKindVersionsGetQueryKey(kind),
    })
  }
}

export function useSavePromptVersion(): UseMutationResult<
  unknown,
  ApiRequestError,
  { kind: string; data: AgentPromptCreate }
> {
  const invalidate = useInvalidatePrompt()
  return useCreateVersionApiAgentPromptsKindVersionsPost({
    mutation: {
      onSuccess: (_data, variables) => { invalidate(variables.kind) },
    },
  })
}

// Typed response, unlike the save hook: activating an OLDER revision changes
// what is live to text the editor is not currently showing, so the caller has
// to re-seed its box from the revision that just went live.
export function useActivatePromptVersion(): UseMutationResult<
  activateVersionApiAgentPromptsKindVersionsVersionActivatePostResponse,
  ApiRequestError,
  { kind: string; version: number }
> {
  const invalidate = useInvalidatePrompt()
  return useActivateVersionApiAgentPromptsKindVersionsVersionActivatePost({
    mutation: {
      onSuccess: (_data, variables) => { invalidate(variables.kind) },
    },
  })
}

export function useTestPrompt(): UseMutationResult<
  testPromptApiAgentPromptsKindTestPostResponse,
  ApiRequestError,
  { kind: string; data: PromptTestRequest }
> {
  // No invalidation: a dry run writes nothing, so no cached state is stale.
  return useTestPromptApiAgentPromptsKindTestPost()
}
